import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface MaskedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mask?: "phone" | "zipcode" | "ssn";
  onValueChange?: (value: string, formattedValue: string) => void;
}

// Masking functions
const formatPhone = (value: string) => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
};

const formatZipCode = (value: string) => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length <= 5) {
    return cleaned;
  }
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}`;
};

const formatSSN = (value: string) => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  }
};

const applyMask = (value: string, mask: string) => {
  switch (mask) {
    case "phone":
      return formatPhone(value);
    case "zipcode":
      return formatZipCode(value);
    case "ssn":
      return formatSSN(value);
    default:
      return value;
  }
};

const getCleanValue = (value: string, mask: string) => {
  switch (mask) {
    case "phone":
    case "zipcode":
    case "ssn":
      return value.replace(/\D/g, "");
    default:
      return value;
  }
};

const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ className, type, mask, onValueChange, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (mask) {
        const formatted = applyMask(inputValue, mask);
        const cleaned = getCleanValue(inputValue, mask);

        // Update the input value with formatted version
        e.target.value = formatted;

        // Call the custom callback with both clean and formatted values
        onValueChange?.(cleaned, formatted);
      }

      // Call the original onChange
      onChange?.(e);
    };

    return (
      <Input
        type={type}
        className={cn(className)}
        ref={ref}
        onChange={handleChange}
        {...props}
      />
    );
  },
);

MaskedInput.displayName = "MaskedInput";

export { MaskedInput };
